import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { inventoryAuditsApi } from '../inventoryAuditApi';
import type { InventoryAuditDetail, InventoryAuditFacilityRef } from '../inventoryAuditTypes';

/**
 * Begin counting: capture the baseline and open the count sheet.
 *
 * <b>This is the irreversible half of an audit, and the modal exists to say so.</b> The figure
 * captured here is what every variance on the sheet is measured against for good — there is no
 * re-snapshot — so it is its own act under its own key rather than a side effect of opening the
 * profile.
 *
 * <b>Starting EARLY is allowed and must be acknowledged.</b> Pulling a count forward is a real
 * thing an auditor does; doing it by a stray click is not, and the server refuses without the
 * acknowledgement rather than trusting the form to have asked.
 */
export function StartInventoryAuditModal({
  open,
  auditId,
  auditNumber,
  auditStartDate,
  facilities,
  onClose,
  onStarted,
}: {
  open: boolean;
  auditId: string;
  auditNumber: string;
  auditStartDate: string;
  facilities: InventoryAuditFacilityRef[];
  onClose: () => void;
  onStarted: (audit: InventoryAuditDetail) => void;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    setConfirmed(false);
    setError(null);
  }, [open]);

  /*
    Read as a CALENDAR comparison rather than an instant one — `auditStartDate` is a day, and
    putting it through the instant path would call a same-day start "early" for any reader west
    of UTC. The server decides in Asia/Manila; this only decides what the form says.
  */
  const today = new Date();
  const localToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const isEarly = localToday < auditStartDate.slice(0, 10);

  async function start() {
    if (!confirmed || saving) return;

    setSaving(true);
    setError(null);

    try {
      onStarted(await inventoryAuditsApi.start(auditId, { confirmEarlyStart: isEarly }));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not start the count.', 0));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Start count"
      size="md"
      id="inventory-audit-start-modal"
      confirm={{
        id: 'inventory-audit-start-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="inventory-audit-start-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="inventory-audit-start-submit"
            variant="primary"
            onClick={() => void start()}
            disabled={!confirmed || saving}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The audit is not asked for — the reader chose it by opening a row's menu. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Audit', value: auditNumber, mono: true },
            { label: 'Window opens', value: formatDateOnly(auditStartDate), mono: true },
            {
              label: 'Stores',
              value: facilities.map((f) => f.storageFacilityName).join(', ') || null,
            },
          ]}
        />

        <p className="text-sm text-slate-600">
          Starting captures what the system believes is on hand for every stock-holding item at
          those stores, and opens the count sheet. That baseline is what every variance is
          measured against — it is captured once and never retaken.
        </p>

        <p className="text-sm text-slate-600">
          <b>No stock moves.</b> An audit records what was counted; correcting the shelf to match
          is a stock adjustment, raised separately.
        </p>

        {isEarly ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            This audit is scheduled to open on <b>{formatDateOnly(auditStartDate)}</b>. Starting now
            takes the baseline earlier than planned, and every count on the sheet will be measured
            against today's figures rather than that day's.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
